/// <amd-module name="Build/Pipelines/Components/ScheduledRunsPanel" />
import { ScheduledRun } from "Build/Client/Contracts/DataProvider";
import { IPipelineScheduledRunsDataService } from "Build/Pipelines/PipelineScheduledRunsDataService";
import * as Resources from "Build/Pipelines/Resources";
import * as React from "react";
import { format } from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IButtonProps } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { TitleSize } from "VSSUI/Header";
import { Icon, IconSize } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { IListItemDetails, ListItem, ScrollableList } from "VSSUI/List";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

interface ScheduledRunsProps {
    isOpen: boolean;
    onDismiss: () => void;
    definitionId: number;
}

const learnMoreUrl = "https://go.microsoft.com/fwlink/?linkid=2138707";

export class ScheduledRunsPanel extends VssComponent<ScheduledRunsProps> {
    private _dataService: IPipelineScheduledRunsDataService;

    constructor(props: ScheduledRunsProps, context: IVssComponentContext) {
        super(props, context);
        this._dataService = this.context.pageContext.getService<IPipelineScheduledRunsDataService>("IPipelineScheduledRunsDataService");
        this._dataService.refreshData(this.props.definitionId, false);
    }

    public render() {
        return (
            <Observer data={this._dataService.data} ready={this._dataService.data.ready} hasDesignerSchedule={this._dataService.hasDesignerSchedule}>
                {(observerProps: { data: ScheduledRun[]; ready: Boolean; hasDesignerSchedule: Boolean }) => {
                    const footerButtons: IButtonProps[] = [
                        {
                            text: Resources.SyncSchedules,
                            primary: true,
                            disabled: !observerProps.ready || observerProps.data.length > 0 || observerProps.hasDesignerSchedule == true,
                            onClick: () => {
                                this._dataService.refreshData(this.props.definitionId, true);
                            }
                        },
                        {
                            text: Resources.OkButtonText,
                            disabled: false,
                            onClick: this.props.onDismiss
                        }
                    ];

                    return (
                        <div>
                            {this.props.isOpen && (
                                <Panel
                                    titleProps={{ text: Resources.PipelineScheduledRunsPanelTitle, size: TitleSize.Large }}
                                    onDismiss={this._onDismiss}
                                    footerButtonProps={footerButtons}
                                >
                                    {observerProps.ready ? (
                                        this._renderBody(observerProps.data)
                                    ) : (
                                        <div className="justify-center">
                                            <Spinner className="absolute-fill" size={SpinnerSize.large} />
                                        </div>
                                    )}
                                </Panel>
                            )}
                        </div>
                    );
                }}
            </Observer>
        );
    }

    private _renderBody(scheduledRuns?: ScheduledRun[]): JSX.Element {
        return (
            <div className="ci-scheduled-runs-panel-body">
                {scheduledRuns != undefined && scheduledRuns.length > 0 && (
                    <Card className="scheduled-run-card flex-grow secondary-text">
                        <div>
                            <span dangerouslySetInnerHTML={{ __html: Resources.ScheduledRunsPanelDescWithData }} />
                            <ScrollableList
                                itemProvider={new ArrayItemProvider(scheduledRuns ? scheduledRuns : [])}
                                renderRow={this.renderRow}
                                width="90%"
                            />
                        </div>
                    </Card>
                )}
                {(scheduledRuns == undefined || scheduledRuns.length == 0) && (
                    <Card className="scheduled-run-card flex-grow secondary-text">
                        <span dangerouslySetInnerHTML={{ __html: Resources.ScheduledRunsPanelDescWithoutData }} />
                    </Card>
                )}
            </div>
        );
    }

    private renderRow = (index: number, item: ScheduledRun, details: IListItemDetails<ScheduledRun>, key?: string): JSX.Element => {
        let scheduledTime: React.ReactNode;
        let cronSyntax = "";
        let countInfo: React.ReactNode;

        if (item.scheduledTime) {
            scheduledTime = item.scheduledTime.map(item => (
                <span className="scheduled-run-card scheduled-time fontSizeMS font-size-ms text-ellipsis secondary-text">
                    {item.toLocaleString()}
                </span>
            ));
            if (item.jobCount) {
                var info = format(Resources.ScheduledRunsPanelJobCount, item.jobCount);
                countInfo = (
                    <MessageCard severity={MessageCardSeverity.Info} className="scheduled-run-card info-card-padding info-margins">
                        {info}
                        <Link href={learnMoreUrl} target="_blank">
                            {Resources.LearnMore}
                        </Link>
                    </MessageCard>
                );
            }
            if (!item.scheduleName && !item.cronSyntax) {
                item.scheduleName = Resources.ScheduledRunsPanelDesignerSchedule;
            }
        } else {
            scheduledTime = (
                <MessageCard severity={MessageCardSeverity.Warning} className="scheduled-run-card">
                    <FormatComponent format={Resources.ScheduledRunsPanelExceededJobLimit}>
                        <Link href={learnMoreUrl} target="_blank">
                            {Resources.LearnMore}
                        </Link>
                    </FormatComponent>
                </MessageCard>
            );
        }
        if (item.cronSyntax) {
            cronSyntax = "[" + item.cronSyntax + "]";
        }
        return (
            <ListItem key={key || "list-item" + index} index={index} details={details}>
                <span className="list-example-row flex-row h-scroll-hidden title-s">
                    <div style={{ marginLeft: "0px", padding: "10px 0px" }} className="flex-column h-scroll-hidden">
                        <span className="scheduled-run-card rhythm-horizontal-8 text-ellipsis">
                            <Icon iconName="Calendar" size={IconSize.small} />
                            <span>{item.scheduleName}</span>
                            <span>{cronSyntax}</span>
                        </span>
                        <span className="rhythm-horizontal-8 padding-bottom-4 fontSizeMS font-size-ms text-ellipsis secondary-text">
                            <Icon iconName="OpenSource" size={IconSize.small} />
                            <span>{item.branch}</span>
                        </span>
                        {scheduledTime}
                        <span className="padding-top-8">{countInfo}</span>
                    </div>
                </span>
            </ListItem>
        );
    };

    private _onDismiss = () => {
        this.props.onDismiss();
    };
}
